import React, { ReactElement, useState, useMemo, useRef, Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs } from "../../../Styles";
import { NewInput } from "../../BaseComponents";
import { ButtonTypes, NewFieldStyles, NewInputHandles } from "../../../Definitions";
import { useWidget } from "../../../Services/Agrisys_GqlDataCaller/WidgetCalls/useWidget";
import { Graph_QL } from "../../../Services";
import { Widget } from "../../../Services/Agrisys_GqlDataCaller/WidgetCalls/GQLCalls/wigdetCalls";
import { useAppSelector } from "../../../Store";
import NewButton, { NewButtonList } from "../../BaseComponents/Buttons/newButton";

interface Widget_Props {
  oneContent?: ReactElement;
  leftContent?: ReactElement;
  rightContent?: ReactElement;
  editMode?: {
    editHeader?: boolean;
    editOneContent?: ReactElement;
    editLeftContent?: ReactElement;
    editRightContent?: ReactElement;
  };
  onCancel: () => void;
  changeData: Dispatch<SetStateAction<Widget>>
  overrides?: {
    baseColor?: string,
    oneContentWidth?: string
  }
  widget: Widget
}
/**
 * EnhancedWidget
 * @param header - The main title text displayed at the top of the widget.
 * @param leftContent - The React element to be rendered in the left section of the widget's content area.
 * @param rightContent - The React element to be rendered in the right section of the widget's content area.
 * @param editMode - An optional object defining the editable states and content for the widget.
 * @param editMode.editHeader - A boolean indicating whether the widget's header can be edited.
 * @param editMode.editLeftContent - The React element to display in the left content area when the widget is in edit mode.
 * @param editMode.editRightContent - The React element to display in the right content area when the widget is in edit mode.
 * @param onSave - A callback function executed when the widget's save button is clicked.
 * @returns A memoized React component providing a structured frame for widgets with display and optional editing capabilities.
 */
const EnhancedWidget = ({ leftContent, rightContent, editMode, onCancel, changeData, widget, oneContent, overrides }: Widget_Props) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const nameRef = useRef<NewInputHandles>(null)
  const [error, setError] = useState<boolean>(false)

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const { domain, connection } = useMemo(() => {
    let domain = Graph_QL.ObjToString({ domain: farm });
    let connection = Graph_QL.create({ domain: farm });
    return { domain, connection };
  }, [farm]);
  
  const { maintainEntries: maintainWidget } = useWidget(connection)
  
  const _baseColor = overrides&&overrides.baseColor ? overrides.baseColor : colors.greyLightTertiary

  // Save
  const handleSaveClick = () => {
    if (nameRef.current?.value === "") {
      setError(true)
      setTimeout(() => {
        setError(false);
      }, 2000);
    };
    if (nameRef.current?.value !== "" && maintainWidget) {
      maintainWidget({ data: [{ ...widget }] })
      setIsEditing(false);
    };
    return;
  };

  // Cancel
  const handleCancelClick = () => {
    onCancel();
    setIsEditing(false);
  };

  // Edit
  const handleEditClick = () => {
    setIsEditing(true);
  };
  
  // Buttons
  const Buttons = () => {
    return (
      <div style={{}}>
        {!isEditing ? (
          <NewButton
            content={t("buttons.edit", { defaultValue: "edit" })}
            startEnhancer={glyphs.Edit({ fillColor: colors.greyLightTertiary })}
            buttonType={ButtonTypes.tableButton}
            onClick={handleEditClick}
          />
        ) : (
          <NewButtonList
            contents={[t("buttons.save", { defaultValue: "save" }), t("buttons.cancel", { defaultValue: "cancel" }), t("buttons.edit", { defaultValue: "edit" })]}
            onClick={(i) => {
              if (i === 0) { handleSaveClick() }
              else if (i === 1) { handleCancelClick() }
            }}
            buttonType={ButtonTypes.tableButton}
            disabled={[false, false, true]}
            overrides={(index) => ({
              baseStyles: { backgroundColor: index === 2 ? colors.greyPrimary : colors.greySecondary },
              disabledStyles: { opacity: index === 2 ? 1 : 0.5 },
            })}
            startEnhancers={[
              glyphs.Check({ fillColor: colors.greyLightTertiary }),
              glyphs.Close({ fillColor: colors.greyLightTertiary }),
              glyphs.Edit({ fillColor: colors.greyLightTertiary }),
            ]}
          />
        )}
      </div>
    );
  };

  return (
    <div style={{ boxSizing: "border-box", /*minHeight: "300px",*/ height: "fit-content", width: "100%", maxWidth: "700px", maxHeight: "720px", position: "relative"}}>
      
      {/* Header / Header Input / Buttons */}
      <div style={{ boxSizing: "border-box", display: "flex", justifyContent: "space-between", padding: "0px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", borderTop: "5px solid " + colors.greyPrimary }}>
          {isEditing && editMode?.editHeader ? (
            <NewInput
              ref={nameRef}
              onChange={(e) => { changeData({ ...widget, name: e }) }}
              isClearable
              isRequired
              fieldOverrides={{ fieldStyle: NewFieldStyles.transparent, fieldWidth: "100%" }}
              valueOverrides={{ defaultValue: widget.name }}
            />
          ) : (
            <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingLeft: "5px" }}>
              {widget.name}
            </p>
          )}
        </div>
        <Buttons />
      </div>
      
      {/* Content / Left / Right */}
      <div style={{ borderRadius: "10px", display: "flex", flexWrap: "wrap", border: "5px solid " + colors.greyPrimary, boxSizing: "border-box", overflow: "hidden", width: "100%", backgroundColor: _baseColor }}>
        
        {(!isEditing && oneContent) && <div style={{ boxSizing: "border-box", minWidth: "250px", minHeight: "250px", overflow: "hidden", margin: "0px auto", width: overrides?.oneContentWidth ?? "100%"}}>{oneContent}</div>}
        {(isEditing && editMode?.editOneContent) && <div style={{ boxSizing: "border-box", height: "fit-content", minWidth: "250px", minHeight: "250px", width: "100%", overflow: "hidden", margin: "0px auto" }}>{editMode?.editOneContent}</div>}

        {(!isEditing && leftContent) && <div style={{ boxSizing: "border-box", height: "250px", overflow: "hidden", flex: "0 0 320px", margin: "0px auto" }}>{leftContent}</div>}
        {(isEditing && editMode?.editLeftContent) && <div style={{ boxSizing: "border-box", height: "250px", overflow: "hidden", width: "100%", margin: "0px auto" }}>{editMode?.editLeftContent}</div>}  
        
        {(!isEditing && rightContent) && <div style={{ boxSizing: "border-box", overflow: "hidden", minHeight: "250px", minWidth: "250px", backgroundColor: colors.greyPrimary, flex: "1 1 auto" }}>{rightContent}</div>}
        {(isEditing && editMode?.editRightContent) && <div style={{ boxSizing: "border-box", overflow: "hidden", minHeight: "250px", minWidth: "250px", width: "100%", backgroundColor: colors.greyPrimary, flex: "1 1 auto" }}>{editMode?.editRightContent}</div>}

      </div>
      {error ? <div style={{width: "100%", position: "absolute", bottom: "-20px"}}><p style={{...fonts.fontSize4, ...fonts.header, color: colors.greyLightTertiary, textAlign: "center"}}>Widget name missing</p></div> : <></>}
    </div>
  );
};

export default EnhancedWidget;